import { v2ServicePath } from '../config';

const apiUrl = () => process.env.REACT_APP_API_URL || 'http://localhost:3001';

class ProblemRcaApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ProblemRcaApiError';
    this.status = status;
    this.data = data;
  }
}

async function safeJson(resp) {
  try {
    return await resp.json();
  } catch {
    return null;
  }
}

async function getJson(url, jwt, signal) {
  const opts = { headers: { Authorization: `Bearer ${jwt}` } };
  if (signal) opts.signal = signal;
  const resp = await fetch(url, opts);
  const data = await safeJson(resp);
  if (!resp.ok) throw new ProblemRcaApiError(data?.error || `HTTP ${resp.status}`, resp.status, data);
  return data;
}

async function postJson(url, jwt, body, signal) {
  const opts = {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body || {}),
  };
  if (signal) opts.signal = signal;
  const resp = await fetch(url, opts);
  const data = await safeJson(resp);
  if (!resp.ok) throw new ProblemRcaApiError(data?.error || `HTTP ${resp.status}`, resp.status, data);
  return data;
}

function serviceUrl(appId, path, env, range, extra = {}) {
  const qs = new URLSearchParams();
  if (env) qs.set('env', env);
  if (range) qs.set('range', range);
  Object.entries(extra).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') qs.set(key, value);
  });
  return `${apiUrl()}${v2ServicePath(appId, path)}?${qs.toString()}`;
}

export async function fetchProblemRcas(
  appId,
  env,
  range,
  jwt,
  { signal, classifierKey = '', limit = 100 } = {},
) {
  if (!appId) return { rcas: [], nextCursor: null, meta: null };
  const raw = await getJson(
    serviceUrl(appId, '/rcas', env, range, {
      classifier_key: classifierKey,
      limit,
    }),
    jwt,
    signal,
  );
  return {
    rcas: Array.isArray(raw.data?.rcas) ? raw.data.rcas : [],
    nextCursor: raw.data?.next_cursor || null,
    meta: raw.meta || null,
  };
}

export async function fetchProblems(
  appId,
  env,
  range,
  jwt,
  { signal, classifierKey = '', limit = 100 } = {},
) {
  if (!appId) return { problems: [], nextCursor: null, meta: null };
  const raw = await getJson(
    serviceUrl(appId, '/problems', env, range, {
      classifier_key: classifierKey,
      limit,
    }),
    jwt,
    signal,
  );
  return {
    problems: Array.isArray(raw.data?.problems) ? raw.data.problems : [],
    nextCursor: raw.data?.next_cursor || null,
    meta: raw.meta || null,
  };
}

export async function fetchProblem(appId, env, range, problemId, jwt, { signal, limit = 100 } = {}) {
  if (!appId || !problemId) return null;
  const raw = await getJson(
    serviceUrl(appId, `/problems/${encodeURIComponent(problemId)}`, env, range, { limit }),
    jwt,
    signal,
  );
  return raw.data || null;
}

export async function fetchProblemRca(appId, env, range, rcaId, jwt, { signal, limit = 100 } = {}) {
  if (!appId || !rcaId) return null;
  const raw = await getJson(
    serviceUrl(appId, `/rcas/${encodeURIComponent(rcaId)}`, env, range, { limit }),
    jwt,
    signal,
  );
  return raw.data || null;
}

export async function fetchProblemEvalSuggestions(
  appId,
  env,
  range,
  problemId,
  jwt,
  { signal, limit = 20 } = {},
) {
  if (!appId || !problemId) return { suggestions: [], nextCursor: null, meta: null };
  const raw = await getJson(
    serviceUrl(appId, `/problems/${encodeURIComponent(problemId)}/eval-suggestions`, env, range, { limit }),
    jwt,
    signal,
  );
  return {
    suggestions: Array.isArray(raw.data?.suggestions) ? raw.data.suggestions : [],
    nextCursor: raw.data?.next_cursor || null,
    meta: raw.meta || null,
  };
}

export async function fetchProblemEvalSuggestion(
  appId,
  env,
  range,
  problemId,
  evalSuggestionId,
  jwt,
  { signal } = {},
) {
  if (!appId || !problemId || !evalSuggestionId) return null;
  const raw = await getJson(
    serviceUrl(
      appId,
      `/problems/${encodeURIComponent(problemId)}/eval-suggestions/${encodeURIComponent(evalSuggestionId)}`,
      env,
      range,
    ),
    jwt,
    signal,
  );
  return raw.data || null;
}

export async function runProblemEvalSuggestions(
  appId,
  env,
  problemId,
  jwt,
  { force = false, signal } = {},
) {
  if (!appId || !problemId) return null;
  const raw = await postJson(
    serviceUrl(appId, `/problems/${encodeURIComponent(problemId)}/eval-suggestions`, env, ''),
    jwt,
    { force },
    signal,
  );
  return raw.data || null;
}

function abortableDelay(ms, signal) {
  const abortError = () => {
    const err = new Error('Aborted');
    err.name = 'AbortError';
    return err;
  };
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const timer = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(abortError());
      }, { once: true });
    }
  });
}

function shouldPollAfterRunError(err) {
  if (err?.name === 'AbortError') return false;
  if (err instanceof ProblemRcaApiError) return err.status === 504 || err.status === 408;
  return err instanceof TypeError;
}

function newestRca(rcas) {
  const values = Array.isArray(rcas) ? rcas.filter(Boolean) : [];
  if (values.length === 0) return null;
  return values.slice().sort((a, b) => (
    new Date(b?.updated_at || b?.created_at || 0).getTime()
    - new Date(a?.updated_at || a?.created_at || 0).getTime()
  ))[0];
}

const RCA_ACTION_POLL_RANGE = 'all-time';

export async function pollProblemRca(
  appId,
  env,
  problemId,
  jwt,
  {
    signal,
    intervalMs = 5000,
    timeoutMs = 180000,
    limit = 100,
  } = {},
) {
  const startedAt = Date.now();
  while (Date.now() - startedAt <= timeoutMs) {
    const problem = await fetchProblem(appId, env, RCA_ACTION_POLL_RANGE, problemId, jwt, { signal, limit });
    const rca = newestRca(problem?.rcas);
    const rcaId = rca?.rca_id || '';
    if (rcaId) {
      const detail = await fetchProblemRca(appId, env, RCA_ACTION_POLL_RANGE, rcaId, jwt, { signal, limit });
      return {
        problem_id: problemId,
        created: false,
        status: 'complete',
        rca: detail || rca,
      };
    }
    await abortableDelay(intervalMs, signal);
  }
  return {
    problem_id: problemId,
    created: false,
    status: 'running',
    timed_out: true,
    rca: null,
  };
}

export async function runProblemRca(appId, env, problemId, jwt, { signal } = {}) {
  if (!appId || !problemId) return null;
  const raw = await postJson(
    serviceUrl(appId, `/problems/${encodeURIComponent(problemId)}/rca`, env, ''),
    jwt,
    {},
    signal,
  );
  return raw.data || null;
}

export async function runProblemRcaAndWait(appId, env, problemId, jwt, { signal } = {}) {
  let result = null;
  try {
    result = await runProblemRca(appId, env, problemId, jwt, { signal });
  } catch (err) {
    if (!shouldPollAfterRunError(err)) throw err;
    return pollProblemRca(appId, env, problemId, jwt, { signal });
  }

  if (result?.status === 'running') {
    return pollProblemRca(appId, env, problemId, jwt, { signal });
  }

  const rcaId = result?.rca?.rca_id || '';
  if (rcaId) {
    const detail = await fetchProblemRca(appId, env, RCA_ACTION_POLL_RANGE, rcaId, jwt, { signal, limit: 100 });
    return {
      ...result,
      status: 'complete',
      rca: detail || result.rca,
    };
  }
  return result;
}

export async function submitProblemResolution(
  appId,
  env,
  problemId,
  jwt,
  { note = '' },
  { signal } = {},
) {
  if (!appId || !problemId) return null;
  const raw = await postJson(
    serviceUrl(appId, `/problems/${encodeURIComponent(problemId)}/resolution`, env, ''),
    jwt,
    { resolved: true, note },
    signal,
  );
  return raw.data || null;
}

export async function submitProblemFeedback(
  appId,
  env,
  problemId,
  jwt,
  { vote, note = '', reason = '' },
  { signal } = {},
) {
  if (!appId || !problemId) return null;
  const raw = await postJson(
    serviceUrl(appId, `/problems/${encodeURIComponent(problemId)}/feedback`, env, ''),
    jwt,
    { vote, note, reason },
    signal,
  );
  return raw.data || null;
}

export async function submitProblemRcaFeedback(
  appId,
  env,
  rcaId,
  jwt,
  { vote, note = '', reason = '' },
  { signal } = {},
) {
  if (!appId || !rcaId) return null;
  const raw = await postJson(
    serviceUrl(appId, `/rcas/${encodeURIComponent(rcaId)}/feedback`, env, ''),
    jwt,
    { vote, note, reason },
    signal,
  );
  return raw.data || null;
}

function isResolved(rca) {
  if (rca?.state === 'resolved' || rca?.status === 'resolved') return true;
  return (Array.isArray(rca?.events) ? rca.events : [])
    .some(event => event?.event_type === 'problem_resolved');
}

function feedbackFromEvents(rca) {
  const latestByUser = new Map();
  const events = Array.isArray(rca?.events) ? rca.events : [];
  events.forEach((event) => {
    const metadata = event?.metadata || {};
    if (metadata.rca_id && metadata.rca_id !== rca?.rca_id) return;
    if (event?.event_type !== 'rca_thumbs_up' && event?.event_type !== 'rca_thumbs_down') return;
    const userId = event?.user_id || '';
    const createdAt = new Date(event?.created_at || 0).getTime();
    const existing = latestByUser.get(userId);
    if (!existing || createdAt >= existing.createdAt) {
      latestByUser.set(userId, { eventType: event.event_type, createdAt });
    }
  });
  const feedback = { thumbs_up: 0, thumbs_down: 0, current_user_vote: '' };
  latestByUser.forEach((entry) => {
    if (entry.eventType === 'rca_thumbs_up') feedback.thumbs_up += 1;
    if (entry.eventType === 'rca_thumbs_down') feedback.thumbs_down += 1;
  });
  return feedback;
}

function signalToInstance(signal, fallback) {
  return {
    trace_id: signal?.trace_id || '',
    service_id: fallback?.representative?.service_id || '',
    service_name: '',
    environment: signal?.environment || fallback?.representative?.environment || '',
    evidence_span_ids: Array.isArray(signal?.evidence_span_ids) ? signal.evidence_span_ids : [],
    updated_at: signal?.trace_start_time || signal?.evaluated_at || signal?.assigned_at || fallback?.last_seen || '',
  };
}

export function normalizeProblemRca(rca) {
  if (!rca) return null;
  const signals = Array.isArray(rca.signals) ? rca.signals : [];
  const instances = signals
    .filter(signal => signal?.trace_id)
    .map(signal => signalToInstance(signal, rca));
  const representativeTraceId = rca.representative_trace_id || rca.representative?.trace_id || '';
  if (instances.length === 0 && representativeTraceId) {
    instances.push({
      trace_id: representativeTraceId,
      service_id: rca.representative?.service_id || '',
      environment: rca.representative?.environment || '',
      evidence_span_ids: [],
      updated_at: rca.updated_at || rca.last_seen || '',
    });
  }

  return {
    ...rca,
    __problemNative: true,
    status: isResolved(rca) ? 'resolved' : (rca.status || rca.state || 'open'),
    service_count: 1,
    feedback: feedbackFromEvents(rca),
    example: {
      trace_id: representativeTraceId,
      environment: rca.representative?.environment || '',
    },
    instances,
    events: Array.isArray(rca.events) ? rca.events : [],
  };
}
